import { useEffect, useState } from "react";
import { Reseller } from "../../models/Reseller";
import { Button, Modal, Space, Table, Typography } from "antd";
import { ResellerFormModal } from "./ResellerFormModal";
import { fetchResellers } from "../../redux/slices/resellerSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { AssignPackagesModal } from "../packages/AssignPackagesModal";

export function ResellersList() {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingReseller, setEditingReseller] = useState<Reseller | null>(null);
  const [businessInfoOpen, setBusinessInfoOpen] = useState(false);
  const [selectedBusinessInfo, setSelectedBusinessInfo] = useState<Reseller | null>(
    null
  );
  const dispatch = useDispatch<AppDispatch>();
  const { resellers, loading } = useSelector((state: RootState) => state.resellers);
  const [assignPackagesModalVisible, setAssignPackagesModalVisible] =
    useState(false);
  const [assigningResellerId, setAssigningResellerId] = useState<number | null>(
    null
  );

  const openAssignPackagesModal = (resellerId: number) => {
    console.log(resellerId);
    setAssigningResellerId(resellerId);
    setAssignPackagesModalVisible(true);
  };
  useEffect(() => {
    dispatch(fetchResellers());
  }, [dispatch]);

  const handleCreate = () => {
    setEditingReseller(null);
    setModalVisible(true);
  };

  const handleEdit = (reseller: Reseller) => {
    setEditingReseller(reseller);
    setModalVisible(true);
  };

  const openBusinessInfo = (reseller: Reseller) => {
    setSelectedBusinessInfo(reseller);
    setBusinessInfoOpen(true);
  };

  const closeBusinessInfo = () => {
    setBusinessInfoOpen(false);
    setSelectedBusinessInfo(null);
  };

  const displayValue = (value?: string | null) => {
    const text = value == null ? "" : String(value).trim();
    return text ? text : "—";
  };

  const displayLocation = (reseller: Reseller) => {
    const parts = [reseller.country, reseller.city]
      .map((value) => (value == null ? "" : String(value).trim()))
      .filter(Boolean);
    return parts.length ? parts.join(" / ") : "—";
  };

  return (
    <div style={{ padding: 24 }}>
      <Button
        type="primary"
        onClick={handleCreate}
        style={{ marginBottom: 16 }}
      >
        Create Reseller
      </Button>
      <Table
        dataSource={resellers}
        rowKey={(r) => r.userId ?? r.resellerId ?? r.email}
        loading={loading}
        scroll={{ x: 1400 }}
        columns={[
          {
            title: "Name",
            dataIndex: "displayName",
            render: (v, r) =>
              v || [r.firstName, r.lastName].filter(Boolean).join(" ") || r.username || "—",
          },
          { title: "Username", dataIndex: "username", render: (v) => v || "—" },
          { title: "Email", dataIndex: "email", render: (v) => v || "—" },
          {
            title: "Business Name",
            dataIndex: "businessName",
            render: (v) => displayValue(v),
          },
          {
            title: "Country / City",
            render: (_, record) => displayLocation(record),
          },
          { title: "Phone", dataIndex: "phone", render: (v) => displayValue(v) },
          { title: "VAT", dataIndex: "vatNumber", render: (v) => displayValue(v) },
          {
            title: "Balance",
            dataIndex: "balance",
            render: (v) => (v != null ? Number(v).toFixed(2) : "—"),
          },
          
          {
            title: "Action",
            render: (_, record) => (
              <Space size="small">
                <Button
                  type="link"
                  onClick={() => {
                    console.log(record);
                    handleEdit(record);
                  }}
                >
                  Edit
                </Button>
                <Button type="link" onClick={() => openBusinessInfo(record)}>
                  View Business Info
                </Button>
                {!record.isAdmin && (
                  <Button
                    type="link"
                    onClick={() => {
                      console.log(record);
                      openAssignPackagesModal(record.resellerId);
                    }}
                  >
                    Assign Packages
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
      />
      <ResellerFormModal
        visible={modalVisible}
        onClose={() => {
          setModalVisible(false);
          fetchResellers();
        }}
        editingReseller={editingReseller}
      />
      {assigningResellerId !== null && (
        <AssignPackagesModal
          visible={assignPackagesModalVisible}
          resellerId={assigningResellerId}
          onClose={() => {
            setAssigningResellerId(null);
            setAssignPackagesModalVisible(false);
          }}
        />
      )}
      <Modal
        open={businessInfoOpen}
        title="Business Info"
        onCancel={closeBusinessInfo}
        footer={[
          <Button key="close" onClick={closeBusinessInfo}>
            Close
          </Button>,
        ]}
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <div>
            <Typography.Text type="secondary">Reseller</Typography.Text>
            <div style={{ fontWeight: 600 }}>
              {selectedBusinessInfo?.displayName ||
                [selectedBusinessInfo?.firstName, selectedBusinessInfo?.lastName]
                  .filter(Boolean)
                  .join(" ") ||
                selectedBusinessInfo?.username ||
                "—"}
            </div>
          </div>

          <div>
            <Typography.Text type="secondary">Business Name</Typography.Text>
            <div>{displayValue(selectedBusinessInfo?.businessName)}</div>
          </div>

          <div>
            <Typography.Text type="secondary">Registration Number</Typography.Text>
            <div>{displayValue(selectedBusinessInfo?.registrationNumber)}</div>
          </div>

          <div>
            <Typography.Text type="secondary">VAT Number</Typography.Text>
            <div>{displayValue(selectedBusinessInfo?.vatNumber)}</div>
          </div>

          <div>
            <Typography.Text type="secondary">Country</Typography.Text>
            <div>{displayValue(selectedBusinessInfo?.country)}</div>
          </div>

          <div>
            <Typography.Text type="secondary">City</Typography.Text>
            <div>{displayValue(selectedBusinessInfo?.city)}</div>
          </div>

          <div>
            <Typography.Text type="secondary">Address</Typography.Text>
            <div>{displayValue(selectedBusinessInfo?.address)}</div>
          </div>

          <div>
            <Typography.Text type="secondary">Phone</Typography.Text>
            <div>{displayValue(selectedBusinessInfo?.phone)}</div>
          </div>

          <div>
            <Typography.Text type="secondary">Profile Email</Typography.Text>
            <div>{displayValue(selectedBusinessInfo?.profileEmail)}</div>
          </div>

          <div>
            <Typography.Text type="secondary">Website</Typography.Text>
            <div>{displayValue(selectedBusinessInfo?.website)}</div>
          </div>
        </Space>
      </Modal>
    </div>
  );
}
